"use client"

import { useEffect, useId, useMemo, useState } from "react"
import type { ConvertTaskToRecurringPayload, Task, UpdateTaskRecurrencePayload } from "@/schema"
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { SimpleFormDialog } from "@/components/dialogs"
import {
  MONTHLY_RULE_TYPE_OPTIONS,
  WEEKDAY_OPTIONS,
  WEEKDAY_ORDINAL_OPTIONS,
  type MonthlyRuleType,
  type Weekday,
  type WeekdayOrdinal,
} from "@/components/tasks/utils/monthly-recurrence"

import { recurrenceDate } from "./utils/deadline-payload"

const INTERVAL_VALUES = Array.from({ length: 12 }, (_, index) => String(index + 1))
const UNIT_OPTIONS = [
  { value: "days", label: "day", pluralLabel: "days" },
  { value: "weeks", label: "week", pluralLabel: "weeks" },
  { value: "months", label: "month", pluralLabel: "months" },
  { value: "years", label: "year", pluralLabel: "years" },
] as const

type IntervalUnit = (typeof UNIT_OPTIONS)[number]["value"]

interface MakeRecurringDialogProps {
  task: Task
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ConvertTaskToRecurringPayload | UpdateTaskRecurrencePayload) => void
  submitting: boolean
  mode?: "create" | "edit"
}

export function MakeRecurringDialog({
  task,
  open,
  onOpenChange,
  onSubmit,
  submitting,
  mode = "create",
}: MakeRecurringDialogProps) {
  const dialogFormId = useId()

  const recurrence = task.task_recurrence
  const initialStartDate = useMemo(
    () =>
      recurrence?.start_at ? new Date(recurrence.start_at) : task.deadline_at ? new Date(task.deadline_at) : undefined,
    [recurrence?.start_at, task.deadline_at]
  )
  const initialEndDate = useMemo(
    () => (recurrence?.recurring_until ? new Date(recurrence.recurring_until) : undefined),
    [recurrence?.recurring_until]
  )
  const [dueDate, setDueDate] = useState<Date | undefined>(initialStartDate)
  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate)
  const [intervalValue, setIntervalValue] = useState(recurrence?.interval_value ?? 1)
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(recurrence?.interval_unit ?? "weeks")
  const [monthlyRuleType, setMonthlyRuleType] = useState<MonthlyRuleType>(
    recurrence?.monthly_rule_type ?? "calendar_day"
  )
  const [weekday, setWeekday] = useState<Weekday | null>(recurrence?.weekday ?? null)
  const [weekdayOrdinal, setWeekdayOrdinal] = useState<WeekdayOrdinal | null>(recurrence?.weekday_ordinal ?? null)

  useEffect(() => {
    if (!open) return

    setDueDate(initialStartDate)
    setEndDate(initialEndDate)
    setIntervalValue(recurrence?.interval_value ?? 1)
    setIntervalUnit(recurrence?.interval_unit ?? "weeks")
    setMonthlyRuleType(recurrence?.monthly_rule_type ?? "calendar_day")
    setWeekday(recurrence?.weekday ?? null)
    setWeekdayOrdinal(recurrence?.weekday_ordinal ?? null)
  }, [
    initialEndDate,
    initialStartDate,
    open,
    recurrence?.interval_unit,
    recurrence?.interval_value,
    recurrence?.monthly_rule_type,
    recurrence?.weekday,
    recurrence?.weekday_ordinal,
  ])

  const handleIntervalUnitChange = (value: string) => {
    const nextUnit = value as IntervalUnit
    setIntervalUnit(nextUnit)
    if (nextUnit === "years") setIntervalValue(Math.min(intervalValue, 2))
    if (nextUnit !== "months" && !(nextUnit === "years" && monthlyRuleType === "last_day")) {
      setMonthlyRuleType("calendar_day")
      setWeekday(null)
      setWeekdayOrdinal(null)
    }
  }

  const handleMonthlyRuleTypeChange = (value: string) => {
    const nextType = value as MonthlyRuleType
    setMonthlyRuleType(nextType)
    if (nextType === "day_of_week") {
      setWeekday(weekday ?? "monday")
      setWeekdayOrdinal(weekdayOrdinal ?? "first")
      return
    }

    setWeekday(null)
    setWeekdayOrdinal(null)
  }

  const handleSubmit = () => {
    if (!dueDate) return
    if (intervalUnit === "months" && monthlyRuleType === "day_of_week" && (!weekday || !weekdayOrdinal)) return

    const allDay = task.all_day ?? true

    onSubmit({
      interval_value: intervalValue,
      interval_unit: intervalUnit,
      monthly_rule_type:
        intervalUnit === "months" || (intervalUnit === "years" && monthlyRuleType === "last_day")
          ? monthlyRuleType
          : "calendar_day",
      weekday: intervalUnit === "months" && monthlyRuleType === "day_of_week" ? weekday : null,
      weekday_ordinal: intervalUnit === "months" && monthlyRuleType === "day_of_week" ? weekdayOrdinal : null,
      start_at: recurrenceDate(dueDate, allDay),
      ...(endDate
        ? { recurring_until: recurrenceDate(endDate, allDay) }
        : mode === "edit"
          ? { recurring_until: null }
          : {}),
    })
  }

  const disableEndDateBeforeStart = (date: Date) => {
    if (!dueDate) return false
    const start = new Date(dueDate)
    start.setHours(0, 0, 0, 0)
    return date < start
  }

  const showWeekdayFields = intervalUnit === "months" && monthlyRuleType === "day_of_week"

  return (
    <SimpleFormDialog
      open={open}
      onOpenChange={onOpenChange}
      isolateEvents
      title={mode === "edit" ? "Edit repeating cadence" : "Make recurring"}
      cancel={{ disabled: submitting }}
      action={{
        label: mode === "edit" ? "Update cadence" : "Make recurring",
        form: dialogFormId,
        pending: submitting,
        disabled:
          !dueDate || (intervalUnit === "months" && monthlyRuleType === "day_of_week" && (!weekday || !weekdayOrdinal)),
      }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (
            !dueDate ||
            (intervalUnit === "months" && monthlyRuleType === "day_of_week" && (!weekday || !weekdayOrdinal)) ||
            submitting
          )
            return
          void handleSubmit()
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{mode === "edit" ? "Starts" : "Current due date"}</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 min-w-[140px] justify-start gap-2">
                  <CalendarIcon className="size-3.5" />
                  <span className={cn(!dueDate && "text-muted-foreground")}>
                    {dueDate ? format(dueDate, "MM/dd/yyyy") : "No due date"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Repeats every</span>
            <div className="flex items-center gap-2">
              <Select value={String(intervalValue)} onValueChange={(v) => setIntervalValue(Number(v))}>
                <SelectTrigger className="h-8 w-16">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {(intervalUnit === "years" ? INTERVAL_VALUES.slice(0, 2) : INTERVAL_VALUES).map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={intervalUnit} onValueChange={handleIntervalUnitChange}>
                <SelectTrigger className="h-8 w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {intervalValue === 1 ? option.label : option.pluralLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {intervalUnit === "months" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">On</span>
                <Select value={monthlyRuleType} onValueChange={handleMonthlyRuleTypeChange}>
                  <SelectTrigger className="h-8 w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHLY_RULE_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showWeekdayFields && (
                <div className="flex items-center justify-end gap-2">
                  <Select
                    value={weekdayOrdinal ?? "first"}
                    onValueChange={(v) => setWeekdayOrdinal(v as WeekdayOrdinal)}
                  >
                    <SelectTrigger className="h-8 w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAY_ORDINAL_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={weekday ?? "monday"} onValueChange={(v) => setWeekday(v as Weekday)}>
                    <SelectTrigger className="h-8 w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Until</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 min-w-[140px] justify-start gap-2">
                  <CalendarIcon className="size-3.5" />
                  <span className={cn(!endDate && "text-muted-foreground")}>
                    {endDate ? format(endDate, "MM/dd/yyyy") : "Never"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  disabled={disableEndDateBeforeStart}
                  initialFocus
                />
                {endDate && (
                  <div className="border-t p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      className="w-full"
                      onClick={() => setEndDate(undefined)}
                    >
                      Clear end date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </form>
    </SimpleFormDialog>
  )
}
